import { useState } from "react";
import { useSearchParams, Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../auth/auth-store";
import { useRequestMagicLink, useGetOauthUrl } from "../../hooks/use-auth-mutations";
import { mapAuthErrorToI18nKey } from "../../utils/auth-errors";
import { SUPPORTED_OAUTH_PROVIDERS } from "../../types/auth";
import type {
  AuthView,
  AuthError as AuthErrorType,
  OAuthProvider,
} from "../../types/auth";
import { AuthForm } from "./auth-form";
import { BrandPanel } from "./brand-panel";

/** Simple email regex for client-side validation */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SignInPage(): React.ReactElement {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [view, setView] = useState<AuthView>("form");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<AuthErrorType | null>(null);
  const [oauthConnecting, setOauthConnecting] = useState<OAuthProvider | null>(
    null,
  );

  const [requestMagicLink, { loading: isSubmitting }] = useRequestMagicLink();
  const [getOauthUrl] = useGetOauthUrl();

  const redirectTo = searchParams.get("redirect");

  // FR-015: Redirect authenticated users away
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  function handleEmailChange(value: string) {
    setEmail(value);
    if (emailError) {
      setEmailError(null);
    }
  }

  async function handleSubmitEmail(submittedEmail: string) {
    // Client-side validation
    if (!EMAIL_REGEX.test(submittedEmail)) {
      setEmailError(t("auth.errors.invalidEmailFormat"));
      return;
    }

    setEmailError(null);

    // Write redirectTo to sessionStorage for post-verify redirect
    if (redirectTo) {
      sessionStorage.setItem("magic_link_redirect", redirectTo);
    }

    try {
      await requestMagicLink({
        variables: { input: { email: submittedEmail } },
      });
      setView("sent");
    } catch (err: unknown) {
      // Apollo Client 4.x throws CombinedGraphQLErrors with `errors` property
      const gqlError = err as {
        errors?: Array<{
          extensions?: Record<string, unknown>;
        }>;
      };

      const ext = gqlError?.errors?.[0]?.extensions;
      const code =
        ext && typeof ext["code"] === "string" ? ext["code"] : "UNKNOWN";
      const isNetworkError =
        !gqlError?.errors?.length &&
        err instanceof Error &&
        err.message.toLowerCase().includes("network");

      const messageKey = isNetworkError
        ? "auth.errors.networkError"
        : mapAuthErrorToI18nKey(code, "magicLink");

      setError({ code: isNetworkError ? "NETWORK_ERROR" : code, messageKey });
      setView("error");
    }
  }

  async function handleOAuthClick(provider: OAuthProvider) {
    // Silent no-op for unsupported providers
    if (!SUPPORTED_OAUTH_PROVIDERS.includes(provider)) return;
    // Already connecting — block
    if (oauthConnecting !== null) return;

    setOauthConnecting(provider);

    try {
      const result = await getOauthUrl({
        variables: {
          input: { provider: provider.toUpperCase() },
        },
      });

      if (result.data?.getOauthUrl) {
        const { authorizationUrl, state } = result.data.getOauthUrl;

        // Write CSRF state and redirect URL to sessionStorage
        sessionStorage.setItem("oauth_state", state);
        if (redirectTo) {
          sessionStorage.setItem("oauth_redirect", redirectTo);
        }

        // Full-page redirect to OAuth provider
        window.location.href = authorizationUrl;
      }
    } catch (err: unknown) {
      const gqlError = err as {
        errors?: Array<{
          extensions?: Record<string, unknown>;
        }>;
      };

      const ext = gqlError?.errors?.[0]?.extensions;
      const code =
        ext && typeof ext["code"] === "string" ? ext["code"] : "UNKNOWN";

      const messageKey = mapAuthErrorToI18nKey(code, "oauth");
      setError({ code, messageKey });
      setView("error");
      setOauthConnecting(null);
    }
  }

  function handleBackToForm() {
    setView("form");
    setError(null);
  }

  function handleRetry() {
    setView("form");
    setError(null);
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col lg:flex-row selection:bg-success/10">
      {/* Left panel — brand (hidden on mobile) */}
      <BrandPanel />

      {/* Right panel — auth form */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12">
        <AuthForm
          view={view}
          email={email}
          emailError={emailError}
          isSubmitting={isSubmitting}
          oauthConnecting={oauthConnecting}
          error={error}
          onEmailChange={handleEmailChange}
          onSubmitEmail={handleSubmitEmail}
          onOAuthClick={handleOAuthClick}
          onBackToForm={handleBackToForm}
          onRetry={handleRetry}
        />
      </div>
    </div>
  );
}
